import { computed, type Ref } from 'vue'
import { useHttp } from '@/shared/lib'
import { useMutation, useQuery } from '@tanstack/vue-query'
import type {
  InventoryFilter,
  InventoryMapBoundsCoords,
  InventoryMapDataResponse,
  InventoryMapObjectData,
} from './types.ts'

export type LoadInventMapDataParams = {
  bbox?: InventoryMapBoundsCoords
  polygon?: [number, number][]
  zoom: number
  type_ids: number[]
  exclude_ids: number[]
  field_filters: { field_id: number; entry_ids: number[] }[]
  grid_px?: number
  /** Id стопок (`colocated`-кластеров), которые нужно раскрыть — сервер добавит им `members`. */
  expand?: string[]
  spider_px?: number
}

/**
 * Приводит клиентские фильтры по полям (`inventory_type_id`/`field_id`|`props_id`/`values`)
 * к серверной форме `{field_id, entry_ids}` — уточнить у бэка судьбу `props_id`
 * (псевдо-размещений полей), в контракте `/objects/map` его нет.
 */
export const toServerFieldFilters = (
  fieldFilters: InventoryFilter['fieldFilters'],
): LoadInventMapDataParams['field_filters'] =>
  (fieldFilters ?? [])
    .map((item) => ({
      field_id: item.field_id ?? item.props_id,
      entry_ids: item.values,
    }))
    .filter((item): item is { field_id: number; entry_ids: number[] } => item.field_id != null)

/**
 * Функция загрузки объектов и кластеров вьюпорта карты (серверная кластеризация)
 */
export const useLoadInventMapData = () => {
  const http = useHttp()

  const mutation = useMutation({
    mutationFn: (data: LoadInventMapDataParams) =>
      http.post('/api/v1/objects/map', data) as Promise<{ data: InventoryMapDataResponse }>,
  })

  const send = async (data: LoadInventMapDataParams) => {
    await mutation.mutateAsync(data)
  }

  return {
    ...mutation,

    send,
  }
}

export type LoadInventRawObjectsParams = {
  bbox?: InventoryMapBoundsCoords
  circle?: { lon: number; lat: number; radius_m: number }
  polygon?: [number, number][]
  type_ids?: number[]
  field_filters?: LoadInventMapDataParams['field_filters']
  limit?: number
}

/**
 * Функция получения «сырого» (без кластеризации) списка объектов области —
 * нужна выделению (прямоугольник, «выделить по типу»), которое не может
 * полагаться на клиентский список объектов, раз часть из них теперь скрыта в кластерах.
 */
export const useLoadInventRawObjects = () => {
  const http = useHttp()

  const mutation = useMutation({
    mutationFn: (data: LoadInventRawObjectsParams) =>
      http.post('/api/v1/objects/geo', data) as Promise<{ data: InventoryMapObjectData[] }>,
  })

  const send = async (data: LoadInventRawObjectsParams): Promise<InventoryMapObjectData[]> => {
    const result = await mutation.mutateAsync(data)
    return Array.isArray(result?.data) ? result.data : []
  }

  return {
    ...mutation,

    send,
  }
}

/**
 * Функция получения общего охвата (bbox) набора объектов по id
 */
export const useLoadInventSquareCoords = (objectId: Ref<number>, preSelectIds: Ref<string>) => {
  const http = useHttp()

  const url = computed(() => {
    const searchParams = new URLSearchParams({
      ids: preSelectIds.value,
    })
    return `/api/v1/objects/bbox?${searchParams}`
  })

  return useQuery({
    queryKey: ['load_invent_square_coords', objectId, preSelectIds],
    queryFn: () => http.get(url.value),
    /** Запрос границ по объектам нужен только при наличии предвыбранных `ids`. */
    enabled: computed(() => !!preSelectIds.value),
    retry: false,
  })
}
